import { css } from 'aphrodite';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { mainChartColor } from '../../../helpers/chartColors';
import { styleSheet } from './styles';

interface PoliciesByLineOfBusinessChartProps {
	data?: {
		title: string;
		visualizationType: string;
		data: {
			id: string;
			label: string;
			count: number;
			value: number;
		}[];
	};
	isLoading?: boolean;
	className?: string;
}

export const PoliciesByLineOfBusinessChart: React.FC<PoliciesByLineOfBusinessChartProps> = ({
	data,
	isLoading,
	className,
}) => {
	const chartData = React.useMemo<
		{
			id: string;
			label: string;
			count: number;
			value: number;
			color: string;
		}[]
	>(() => {
		if (!data?.data || data.data.length === 0) {
			return [];
		}

		return data.data.map(item => {
			const color = mainChartColor();
			return {
				...item,
				color,
			};
		});
	}, [data]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No policy data available</div>
			</div>
		);
	}

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const tooltipData = payload[0].payload;
			return (
				<div className={css(styleSheet.tooltip)}>
					<p className={css(styleSheet.tooltipLabel)}>{tooltipData.label}</p>
					<p className={css(styleSheet.tooltipValue)}>Count: {tooltipData.count}</p>
				</div>
			);
		}
		return null;
	};

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h3 className={css(styleSheet.title)}>{data?.title || 'Policies by Line of Business'}</h3>
			<ResponsiveContainer width='100%' height={400}>
				<BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 60 }}>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='label'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						angle={-45}
						textAnchor='end'
						height={100}
					/>
					<YAxis stroke={THEME.colors.text.main} tick={{ fontSize: 12, fill: THEME.colors.text.main }} />
					<Tooltip content={<CustomTooltip />} />
					<Bar dataKey='count' radius={[4, 4, 0, 0]}>
						{chartData.map(item => {
							return <Cell key={item.id} fill={item.color} />;
						})}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};
